import { Copy, FileQuestion, Send } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useIncidentReport } from "../../hooks/useIncidentReport";
import { PageMeta } from "../PageMeta";

export function NotFoundPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { report, status: reportStatus } = useIncidentReport();
  const timestamp = new Date().toISOString();
  const isAuthenticated = !!localStorage.getItem("adjudon_token");

  const handleCopy = () => {
    const text = [
      `ADJUDON ERROR REPORT`,
      `STATUS: 404 RESOURCE_NOT_FOUND`,
      `TIME: ${timestamp}`,
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleContactSupport = () => {
    report({ type: "not_found", message: "404 RESOURCE_NOT_FOUND", timestamp });
  };

  const authLinks = [
    { to: "/dashboard", label: "Overview" },
    { to: "/dashboard/systems", label: "AI Systems" },
    { to: "/dashboard/decision-log", label: "Decision Log" },
    { to: "/dashboard/policy-engine", label: "Policies" },
  ];

  const guestLinks = [
    { to: "/pricing", label: "Pricing" },
    { to: "/help", label: "Help" },
    { to: "/", label: "Home" },
  ];

  const navLinks = isAuthenticated ? authLinks : guestLinks;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--background)]">
      <PageMeta
        title="404 – ADJUDON"
        description="The page you're looking for doesn't exist."
        noindex={true}
      />

      <div className="adp-card" style={{ maxWidth: "440px", width: "100%", padding: "40px" }}>
        {/* Icon */}
        <div className="p-3 border border-border bg-surface-1 inline-flex mb-8">
          <FileQuestion className="w-5 h-5 text-muted-foreground" />
        </div>

        {/* Label + Title + Description */}
        <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1">
          Page Not Found
        </div>
        <h1 className="text-xl font-semibold text-foreground mb-3">
          This page doesn't exist
        </h1>
        <p className="text-sm text-muted-foreground" style={{ lineHeight: "1.6", maxWidth: "360px" }}>
          The requested URL could not be found. It may have been moved, renamed, or deleted.
        </p>

        {/* Divider */}
        <div className="border-t border-border my-8" />

        {/* Diagnostics */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
            Diagnostics
          </span>
          <button
            onClick={handleCopy}
            className="adp-btn flex items-center gap-1 px-2 py-1 text-[10px] text-mono uppercase tracking-wider"
          >
            <Copy className="w-3 h-3" />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-6">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Status</span>
            <span className="text-xs text-mono text-foreground">404 RESOURCE_NOT_FOUND</span>
          </div>
          <div className="flex items-start gap-6">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Time</span>
            <span className="text-xs text-mono text-foreground">{timestamp}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-8" />

        {/* Quick Navigation */}
        <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-3">
          Quick Navigation
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {navLinks.map(({ to, label }) => (
            <Link key={to} to={to} className="adp-btn px-4 py-2 text-[11px] text-mono uppercase tracking-wider">
              {label}
            </Link>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => navigate(isAuthenticated ? "/dashboard" : "/")}
            className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider flex items-center justify-center"
          >
            {isAuthenticated ? "Back to dashboard" : "Back to homepage"}
          </button>
          <button
            onClick={handleContactSupport}
            disabled={reportStatus === "sending"}
            className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider flex items-center justify-center gap-2 text-muted-foreground"
          >
            <Send className="w-3.5 h-3.5" />
            {reportStatus === "sending" ? "Sending..." : reportStatus === "sent" ? "Report Sent ✓" : "Contact Support"}
          </button>
        </div>

        {/* Support Footer */}
        <div className="border-t border-border mt-8 pt-4 flex gap-4">
          <a
            href="https://status.adjudon.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            Status.Adjudon.com
          </a>
          <a
            href="mailto:support@adjudon.com"
            className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            Support@Adjudon.com
          </a>
        </div>
      </div>
    </div>
  );
}
